// @ts-check
// Aba "Atividades" das estatísticas (spec 2.0.0, fluxos.md seções 13.4 e 20.13; contrato 09 de fluxos, seção 17): as
// execuções do fluxo com filtros de situação e de passo, cada linha abre os passos percorridos, e as ações "Parar" (quem
// está no fluxo) e "Tentar de novo" (quem falhou). Página de 50, com "Carregar mais".
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import { Link } from '../../app/router.js';
import { getFlowRun, listFlowRuns, retryFlowRuns, stopFlowRun } from '../../data/flows.js';
import { Button } from '../../ui/button.js';
import { EmptyState, Skeleton } from '../../ui/feedback.js';
import { Icon } from '../../ui/icon.js';
import { showErrorSnackbar, showSuccessSnackbar } from '../../ui/snackbar.js';
import { formatDateTime } from '../../lib/format.js';
import { FLOW_RUN_STATUSES, OPEN_FLOW_RUN_STATUSES } from '../../../../shared/flows/flow-reasons.js';
import { flowReasonText, waitResumeText } from './flow-ui.js';
import { t } from '../../i18n/index.js';

/**
 * @typedef {import('../../data/flows.js').FlowRunSummary} FlowRunSummary
 * @typedef {{ id: string, title: string }} NodeChoice
 */

const PAGE_SIZE = 50;

/** O que a linha mostra na coluna "Retoma ou terminou". @param {FlowRunSummary} run */
export function runStateText(run) {
  if (run.status === 'WAITING' || run.status === 'SENDING') return waitResumeText(run);
  if (run.status === 'FAILED') return run.error ?? t('flows.runs.failed');
  if (run.status === 'EXITED') return flowReasonText('EXIT', run.exitReason);
  if (run.status === 'STOPPED') return flowReasonText('STOP', run.exitReason);
  if (run.status === 'COMPLETED') return run.endedAt ? t('flows.runs.completedAt', { date: formatDateTime(run.endedAt) }) : t('flows.runs.statuses.COMPLETED');
  if (run.status === 'PAUSED') return t('flows.runs.paused');
  return '';
}

/**
 * @param {{ flowId: string, nodes: NodeChoice[], initialStatus?: string | null, initialNodeId?: string | null }} props
 */
export function FlowRunsList({ flowId, nodes, initialStatus = null, initialNodeId = null }) {
  const [status, setStatus] = useState(initialStatus ?? '');
  const [nodeId, setNodeId] = useState(initialNodeId ?? '');
  const [runs, setRuns] = useState(/** @type {FlowRunSummary[] | null} */ (null));
  const [cursor, setCursor] = useState(/** @type {string | null} */ (null));
  const [hasMore, setHasMore] = useState(false);
  const [failed, setFailed] = useState(false);
  const [openRunId, setOpenRunId] = useState(/** @type {string | null} */ (null));
  const [steps, setSteps] = useState(/** @type {any[] | null} */ (null));
  const [busyRunId, setBusyRunId] = useState(/** @type {string | null} */ (null));

  /** @param {string | null} after */
  const load = (after = null) => {
    setFailed(false);
    if (!after) setRuns(null);
    listFlowRuns(flowId, { status: status || undefined, nodeId: nodeId || undefined, limit: PAGE_SIZE, startingAfter: after })
      .then((result) => {
        setRuns((current) => (after && current ? [...current, ...result.runs] : result.runs));
        setCursor(result.pageInfo.endCursor);
        setHasMore(result.pageInfo.hasNextPage);
      })
      .catch(() => setFailed(true));
  };
  useEffect(() => load(null), [flowId, status, nodeId]);

  /** @param {string} runId */
  const toggleSteps = async (runId) => {
    if (openRunId === runId) {
      setOpenRunId(null);
      return;
    }
    setOpenRunId(runId);
    setSteps(null);
    try {
      const detail = await getFlowRun(runId);
      setSteps(detail.steps ?? []);
    } catch (error) {
      showErrorSnackbar(error);
      setSteps([]);
    }
  };
  /** @param {FlowRunSummary} run */
  const stop = async (run) => {
    setBusyRunId(run.id);
    try {
      await stopFlowRun(run.id);
      showSuccessSnackbar(t('flows.runs.stopped'));
      load(null);
    } catch (error) {
      showErrorSnackbar(error);
    } finally {
      setBusyRunId(null);
    }
  };
  /** @param {FlowRunSummary} run */
  const retry = async (run) => {
    setBusyRunId(run.id);
    try {
      const count = await retryFlowRuns(flowId, { runIds: [run.id] });
      showSuccessSnackbar(t('flows.runs.retried', { count }));
      load(null);
    } catch (error) {
      showErrorSnackbar(error);
    } finally {
      setBusyRunId(null);
    }
  };

  return html`<div class="flow-runs" data-testid="flow-runs">
    <div class="flow-runs-filters">
      <label class="flow-inline-field">
        <span>${t('flows.runs.status')}</span>
        <select class="text-input" value=${status} onChange=${(/** @type {Event} */ event) => setStatus(/** @type {HTMLSelectElement} */ (event.currentTarget).value)}>
          <option value="">${t('flows.runs.allStatuses')}</option>
          ${FLOW_RUN_STATUSES.map((value) => html`<option key=${value} value=${value}>${t(`flows.runs.statuses.${value}`)}</option>`)}
        </select>
      </label>
      <label class="flow-inline-field">
        <span>${t('flows.runs.node')}</span>
        <select class="text-input" value=${nodeId} onChange=${(/** @type {Event} */ event) => setNodeId(/** @type {HTMLSelectElement} */ (event.currentTarget).value)}>
          <option value="">${t('flows.runs.allNodes')}</option>
          ${nodes.map((node) => html`<option key=${node.id} value=${node.id}>${node.title}</option>`)}
        </select>
      </label>
    </div>
    ${failed
      ? html`<${EmptyState} icon="IconAlertTriangle" title=${t('flows.runs.loadError')}
          action=${html`<${Button} title=${t('flows.common.retry')} variant="secondary" size="small" onClick=${() => load(null)} />`} />`
      : runs === null
        ? html`<div class="flow-runs-skeleton">${[0, 1, 2, 3].map((key) => html`<${Skeleton} key=${key} height=${32} />`)}</div>`
        : runs.length === 0
          ? html`<${EmptyState} icon="IconListDetails" title=${t('flows.runs.empty')} />`
          : html`<ul class="flow-runs-list">
              ${runs.map((run) => {
                const isOpen = openRunId === run.id;
                return html`<li key=${run.id} class="flow-run" data-open=${isOpen ? 'true' : undefined}>
                  <div class="flow-run-row">
                    <button type="button" class="flow-icon-button" aria-expanded=${isOpen ? 'true' : 'false'} aria-label=${t('flows.runs.showSteps', { name: run.cardLabel })}
                      onClick=${() => toggleSteps(run.id)}><${Icon} name=${isOpen ? 'IconChevronDown' : 'IconChevronRight'} size=${16} /></button>
                    <span class="flow-run-card"><${Link} href=${`/object/enrollmentOpportunity/${encodeURIComponent(run.cardId)}`}>${run.cardLabel}<//></span>
                    <span class="flow-run-status" data-status=${run.status}>${t(`flows.runs.statuses.${run.status}`)}</span>
                    <span class="flow-run-node">${run.currentNode?.title ?? (run.currentNode ? t(`flows.nodes.${run.currentNode.type}.name`) : '-')}</span>
                    <span class="flow-run-date">${formatDateTime(run.enteredAt)}</span>
                    <span class="flow-run-state">${runStateText(run)}</span>
                    <span class="flow-run-actions">
                      ${run.status === 'FAILED'
                        ? html`<${Button} title=${t('flows.runs.retry')} variant="secondary" size="small" isLoading=${busyRunId === run.id} onClick=${() => retry(run)} />`
                        : null}
                      ${OPEN_FLOW_RUN_STATUSES.includes(run.status)
                        ? html`<${Button} title=${t('flows.runs.stop')} variant="tertiary" accent="danger" size="small" isLoading=${busyRunId === run.id} onClick=${() => stop(run)} />`
                        : null}
                    </span>
                  </div>
                  ${isOpen
                    ? html`<div class="flow-run-steps">
                        ${steps === null
                          ? html`<${Skeleton} height=${20} />`
                          : steps.length === 0
                            ? html`<p class="flow-field-hint">${t('flows.runs.noSteps')}</p>`
                            : html`<ol>${steps.map((step) => html`<li key=${step.id} data-status=${step.status}>
                                <span>${step.title || (step.nodeType === 'REMOVED' ? t('flows.runs.removedStep') : t(`flows.nodes.${step.nodeType}.name`))}</span>
                                <span class="flow-run-step-status">${t(`flows.runs.stepStatuses.${step.status}`)}</span>
                                ${step.reason ? html`<span class="flow-field-hint">${flowReasonText(step.status === 'FAILED' ? 'FAIL' : step.status === 'EXITED' ? 'EXIT' : 'STEP_SKIP', step.reason)}</span>` : null}
                                ${step.error ? html`<span class="flow-field-error">${step.error}</span>` : null}
                                <span class="flow-run-date">${formatDateTime(step.endedAt ?? step.startedAt)}</span>
                              </li>`)}</ol>`}
                      </div>`
                    : null}
                </li>`;
              })}
            </ul>
            ${hasMore ? html`<${Button} title=${t('flows.runs.loadMore')} variant="secondary" size="small" onClick=${() => load(cursor)} />` : null}`}
  </div>`;
}
